import React, { FC, useCallback, useEffect, useState } from 'react';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import styled from 'styled-components';
import useSound from 'use-sound';
import { CommentsAPI } from 'api/comments';
import { BaseComment, Reaction, UserMessage } from 'api/conversations/types';
import { UserId } from 'api/models';
import { ConversationApi } from 'api/timesheets/conversations';
import { CurrentProjectIdContext } from 'contexts';
import { Empty, Loaded, Loading } from 'core/state';
import { logger } from 'services';
import { TimesheetsEmptyState } from './empty-state.timesheets';
import { TimesheetsHeader } from './header.timesheets';
import { ConversationSkeleton, FetchConversationRow } from './loading';
import { MessagesComment } from './messages-comment';
import { MessagesList } from './messages-list';
import {
  asList,
  hasData,
  isLoading,
  ofId,
  useTimesheetComments,
} from './state';

const Header = styled.div`
  background: var(--coolest-gray-10);
  height: 58px;
  padding: 16px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 243px);
  min-height: 460px;
  width: 400px;
`;

const ListWrapper = styled.div`
  height: 100%;
  overflow: hidden;
  overflow-y: auto;
`;

interface Props {
  userId: UserId;
  onSeen: (comment: BaseComment) => void;
}

export const ConversationPaneTimesheets: FC<Props> = ({
  userId: [, userId],
  onSeen,
}) => {
  const [reply, setReply] = useState<UserMessage>();
  const { comments, create, update, remove } = useTimesheetComments(
    UserId(userId),
  );
  const loading = isLoading(comments);
  const hasLoadedAllComments = hasData(comments);
  const [playSound] = useSound('/sounds/pop0.ogg');
  const [scrollToEnd, setScrollToEnd] = useState(false);
  const messages = asList(comments);

  const onAddReactionToComment = (
    commentId: number,
    reactionType: Reaction,
  ): void => {
    CommentsAPI.addReaction(commentId, reactionType);
    playSound();
    setScrollToEnd(false);
  };

  const clearReply = () => setReply(undefined);

  const addComment = useCallback(
    (comment: Partial<UserMessage>): void => {
      create(comment as any);
      clearReply();
      setScrollToEnd(true);
      setScrollToEnd(false);
    },
    [create],
  );

  const onPerformReply = useCallback(
    (comment: { commentId: number }): void => {
      setScrollToEnd(false);
      const replyMessage = ofId(comment.commentId)(comments)[1];

      setReply(replyMessage as UserMessage);
    },
    [comments],
  );

  const onCommentUpdate = useCallback(
    (message: UserMessage): void => {
      setScrollToEnd(false);
      update(message);
    },
    [update],
  );

  const onPerformDelete = useCallback(
    (commentId: number): void => {
      setScrollToEnd(false);
      remove(commentId);
    },
    [remove],
  );
  const [approverIds, setApproverIds] = useState<
    Empty | Loading | Loaded<number[]>
  >(Empty());

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized) {
      return () => {};
    }
    const subscription = from(ConversationApi.get(UserId(userId), 1))
      .pipe(
        map((result) => result.approverIds),
        map(Loaded),
      )
      .subscribe(
        (ids) => setApproverIds(ids),
        (error: Error) => {
          logger.error('failed to fetch approver ids', { error, userId });
          setApproverIds(Empty());
        },
        () => setInitialized(true),
      );

    return () => subscription.unsubscribe();
  }, [initialized, userId]);

  const fetchAllConversation = useCallback(() => {
    ConversationApi.get(UserId(userId), 1).then((result) => {
      setApproverIds(Loaded(result.approverIds));
    });
    setScrollToEnd(false);
  }, [userId, setApproverIds]);

  return (
    <CurrentProjectIdContext.Provider value={-1}>
      <Wrapper>
        <Header>
          <TimesheetsHeader userId={UserId(userId)} approverIds={approverIds} />
        </Header>
        <ListWrapper>
          {loading ? (
            <ConversationSkeleton />
          ) : (
            <>
              {!hasLoadedAllComments && (
                <FetchConversationRow
                  fetchAllConversation={fetchAllConversation}
                />
              )}
              <MessagesList
                messages={messages}
                onAddReactionToComment={onAddReactionToComment}
                onCommentUpdate={onCommentUpdate}
                onPerformReply={onPerformReply}
                onPerformDelete={onPerformDelete}
                scrollToEnd={scrollToEnd}
                onSeen={onSeen}
                empty={
                  <TimesheetsEmptyState
                    userId={UserId(userId)}
                    approverIds={approverIds}
                  />
                }
                disallowMentions
              />
            </>
          )}
        </ListWrapper>
        <MessagesComment
          disableMentions
          disableAttachments
          hidePrivate
          reply={reply}
          accountId={-1}
          projectId={-1}
          onAddComment={addComment}
          onClearReply={clearReply}
        />
      </Wrapper>
    </CurrentProjectIdContext.Provider>
  );
};
